const CHAT_READ_STORAGE_KEY = "bidz_chat_read_messages";
export const CHAT_READ_STATE_EVENT = "bidz:chat-read-state-changed";

const MAX_READ_KEYS_PER_USER = 1000;

const getStorage = () => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const normalizeValue = (value) => String(value ?? "").trim();

const readState = () => {
  const storage = getStorage();
  if (!storage) return {};

  try {
    const parsed = JSON.parse(storage.getItem(CHAT_READ_STORAGE_KEY) || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const writeState = (state) => {
  const storage = getStorage();
  if (!storage) return;

  try {
    storage.setItem(CHAT_READ_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore storage quota/private mode failures. The in-page state still updates.
  }
};

export const isUnreadChatStatus = (status) => {
  if (status === false || status === 0) return true;
  return String(status ?? "").toLowerCase() === "false";
};

export const getChatMessageReadKey = (message = {}) => {
  const messageId =
    message.id ||
    message.chat_id ||
    message.message_id ||
    message.chat_token ||
    message.token ||
    "";

  if (messageId) return `id:${messageId}`;

  return [
    normalizeValue(message.sender_token),
    normalizeValue(message.receiver_token),
    normalizeValue(message.rfq_id || message.rfq_token),
    normalizeValue(message.created || message.created_date || message.created_on),
    normalizeValue(message.message),
  ].join("|");
};

export const getReadChatKeys = (userToken) => {
  const state = readState();
  const keys = state[normalizeValue(userToken)];
  return new Set(Array.isArray(keys) ? keys : []);
};

export const isChatMessageLocallyRead = (message, userToken) => {
  const key = getChatMessageReadKey(message);
  if (!key.split("|").join("")) return false;
  return getReadChatKeys(userToken).has(key);
};

export const isChatMessageUnreadForUser = (message, userToken) => {
  const isIncoming =
    normalizeValue(message?.receiver_token) === normalizeValue(userToken) &&
    normalizeValue(message?.sender_token) !== normalizeValue(userToken);

  return (
    isIncoming &&
    isUnreadChatStatus(message?.status) &&
    !isChatMessageLocallyRead(message, userToken)
  );
};

export const applyLocalReadStateToMessages = (messages = [], userToken) => {
  const readKeys = getReadChatKeys(userToken);
  return (Array.isArray(messages) ? messages : []).map((message) => {
    const key = getChatMessageReadKey(message);
    return readKeys.has(key) ? { ...message, status: true } : message;
  });
};

export const markChatMessagesReadLocally = (messages = [], userToken) => {
  const normalizedUserToken = normalizeValue(userToken);
  if (!normalizedUserToken) return;

  const existingState = readState();
  const previousKeys = Array.isArray(existingState[normalizedUserToken])
    ? existingState[normalizedUserToken]
    : [];
  const nextKeys = new Set(previousKeys);

  (Array.isArray(messages) ? messages : []).forEach((message) => {
    const isIncoming =
      normalizeValue(message?.receiver_token) === normalizedUserToken &&
      normalizeValue(message?.sender_token) !== normalizedUserToken;

    if (!isIncoming) return;

    const key = getChatMessageReadKey(message);
    if (key.split("|").join("")) nextKeys.add(key);
  });

  existingState[normalizedUserToken] = Array.from(nextKeys).slice(-MAX_READ_KEYS_PER_USER);
  writeState(existingState);

  window.dispatchEvent(
    new CustomEvent(CHAT_READ_STATE_EVENT, {
      detail: { userToken: normalizedUserToken },
    }),
  );
};
